import api from '../../../services/api';

// Action types
export const SET_INBOX_LOADING = 'SET_INBOX_LOADING';
export const SET_INBOX_MESSAGES = 'SET_INBOX_MESSAGES';
export const SET_INBOX_ERROR = 'SET_INBOX_ERROR';
export const SET_SELECTED_INBOX = 'SET_SELECTED_INBOX';
export const SET_INBOX_LOADING_MORE = 'SET_INBOX_LOADING_MORE';
export const APPEND_INBOX_MESSAGES = 'APPEND_INBOX_MESSAGES';
export const SET_INBOX_PAGINATION = 'SET_INBOX_PAGINATION';
export const SET_UNPROCESSED_COUNT = 'SET_UNPROCESSED_COUNT';
export const SET_INBOX_SEARCH_TERM = 'SET_INBOX_SEARCH_TERM';
export const SET_INBOX_SEARCH_LOADING = 'SET_INBOX_SEARCH_LOADING';
export const UPDATE_MESSAGE_IN_STORE = 'UPDATE_MESSAGE_IN_STORE';

// Action creators
export const setInboxLoading = (loading) => ({
  type: SET_INBOX_LOADING,
  payload: loading
});

export const setInboxMessages = (messages) => ({
  type: SET_INBOX_MESSAGES,
  payload: messages
});

export const setInboxError = (error) => ({
  type: SET_INBOX_ERROR,
  payload: error
});

export const setSelectedInbox = (inbox) => ({
  type: SET_SELECTED_INBOX,
  payload: inbox
});

export const setInboxLoadingMore = (loading) => ({
  type: SET_INBOX_LOADING_MORE,
  payload: loading
});

export const appendInboxMessages = (messages) => ({
  type: APPEND_INBOX_MESSAGES,
  payload: messages
});

export const setInboxPagination = (pagination) => ({
  type: SET_INBOX_PAGINATION,
  payload: pagination
});

export const setUnprocessedCount = (count) => ({
  type: SET_UNPROCESSED_COUNT,
  payload: count
});

export const setInboxSearchTerm = (searchTerm) => ({
  type: SET_INBOX_SEARCH_TERM,
  payload: searchTerm
});

export const setInboxSearchLoading = (loading) => ({
  type: SET_INBOX_SEARCH_LOADING,
  payload: loading
});

export const updateMessageInStore = (messageId, updates) => ({
  type: UPDATE_MESSAGE_IN_STORE,
  payload: { messageId, updates }
});

// Fetch inbox messages - get all messages with order and seller account info
export const fetchInboxMessages = (searchTerm = '', isSearch = false) => async (dispatch) => {
  try {
    if (isSearch) {
      dispatch(setInboxSearchLoading(true));
    } else {
      dispatch(setInboxLoading(true));
    }

    const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
    const response = await api.get(`/messages/inbox?page=1&limit=20${searchParam}`);

    if (response.data.success) {
      dispatch(setInboxMessages(response.data.data));
      dispatch(setInboxPagination(response.data.pagination));
      
      // Also update unprocessed count
      if (!isSearch) {
        dispatch(fetchUnprocessedCount());
      }
      
      return {
        success: true,
        data: response.data.data
      };
    } else {
      throw new Error(response.data.message || 'Failed to fetch inbox messages');
    }
  } catch (error) {
    console.error('Error fetching inbox messages:', error);
    dispatch(setInboxError(error.message || 'Error occurred while fetching inbox messages'));
    return {
      success: false,
      message: error.response?.data?.message || 'Error occurred while fetching inbox messages'
    };
  } finally {
    if (isSearch) {
      dispatch(setInboxSearchLoading(false));
    } else {
      dispatch(setInboxLoading(false));
    }
  }
};

// Load more inbox messages for infinite scroll
export const loadMoreInboxMessages = (page, searchTerm = '') => async (dispatch) => {
  try {
    dispatch(setInboxLoadingMore(true));

    const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
    const response = await api.get(`/messages/inbox?page=${page}&limit=20${searchParam}`);

    if (response.data.success) {
      dispatch(appendInboxMessages(response.data.data));
      dispatch(setInboxPagination(response.data.pagination));
      return {
        success: true,
        data: response.data.data
      };
    } else {
      throw new Error(response.data.message || 'Failed to load more inbox messages');
    }
  } catch (error) {
    console.error('Error loading more inbox messages:', error);
    dispatch(setInboxError(error.message || 'Error occurred while loading more inbox messages'));
    return {
      success: false,
      message: error.response?.data?.message || 'Error occurred while loading more inbox messages'
    };
  } finally {
    dispatch(setInboxLoadingMore(false));
  }
};

// Update message process status
export const updateMessageProcessStatus = (messageId, isProcessed) => async (dispatch) => {
  try {
    const response = await api.patch(`/orders/messages/${messageId}/process-status`, {
      isProcessed
    });

    if (response.data.success) {
      // Update the message in store directly without full reload
      dispatch(updateMessageInStore(messageId, { isProcessed }));
      
      // Update unprocessed count after status change
      dispatch(fetchUnprocessedCount());
      
      return {
        success: true,
        message: response.data.message || 'Message status updated successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Failed to update message status'
      };
    }
  } catch (error) {
    console.error('Error updating message process status:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error occurred while updating message status'
    };
  }
};

// Send message to customer
export const sendCustomerMessage = ({ orderId, amazonOrderId, messageContent }) => async (dispatch) => {
  try {
    const response = await api.post('/orders/send-customer-message', {
      orderId,
      amazonOrderId,
      messageContent
    });

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Message sent successfully',
        data: response.data.data
      };
    } else {
      return {
        success: false,
        message: response.data.message || 'Failed to send message'
      };
    }
  } catch (error) {
    console.error('Error sending customer message:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error occurred while sending message'
    };
  }
};

// Get unprocessed message count for header badge
export const fetchUnprocessedCount = () => async (dispatch) => {
  try {
    const response = await api.get('/messages/unprocessed-count');

    if (response.data.success) {
      dispatch(setUnprocessedCount(response.data.count));
      return {
        success: true,
        count: response.data.count
      };
    } else {
      throw new Error(response.data.message || 'Failed to fetch unprocessed count');
    }
  } catch (error) {
    console.error('Error fetching unprocessed count:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Error occurred while fetching unprocessed count'
    };
  }
};